import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import type { Qualification, Progress, Enrollment } from '../types';
import { JQRItemCard } from './JQRItemCard';
import { useTheme, type ThemeColors } from '@flowcore-water/ui';
import { getDeadlineDate, getDaysRemaining, getQualificationTiming } from '../utils/timeline';
import {
  getRetriesNeeded,
  getCompletedCount,
  getProgressPercentage,
  getQualStatus,
  type QualStatus,
} from '../utils/skillProgress';

/* ── ASCII Progress Bar (dynamic width) ── */
const AsciiProgressBar: React.FC<{
  percentage: number;
  completedCount: number;
  totalCount: number;
  isExpanded: boolean;
  t: ThemeColors;
}> = ({ percentage, completedCount, totalCount, isExpanded, t }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [barWidth, setBarWidth] = useState(40);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;
    const charW = measurer.getBoundingClientRect().width;
    if (charW > 0) {
      // subtract 2 for the brackets
      const chars = Math.floor(container.clientWidth / charW) - 2;
      setBarWidth(Math.max(10, chars));
    }
  }, []);

  useEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const filled = Math.round((percentage / 100) * barWidth);
  const empty = barWidth - filled;

  return (
    <div className={`mt-3 transition-opacity duration-300 ${isExpanded ? 'opacity-0' : 'opacity-100'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-mono font-bold uppercase tracking-wide" style={{ color: t.textSecondary }}>
          {String(percentage).padStart(3, '\u2007')}% complete
        </span>
        <span className="text-xs font-mono" style={{ color: t.textMuted }}>
          {completedCount}/{totalCount}
        </span>
      </div>
      <div ref={containerRef} className="font-mono text-xs leading-none select-none" style={{ whiteSpace: 'nowrap' }}>
        {/* Hidden single-char measurer */}
        <span ref={measureRef} style={{ visibility: 'hidden', position: 'absolute' }}>=</span>
        <span style={{ color: t.textMuted }}>[</span>
        {filled > 0 && (
          <span style={{
            color: percentage === 100 ? t.success : t.progressFill,
            textShadow: percentage < 100
              ? `0 0 6px ${t.progressGlow}, 0 0 12px ${t.progressGlow}40`
              : undefined,
          }}>
            {'='.repeat(Math.max(0, filled - (percentage < 100 ? 1 : 0)))}
            {percentage < 100 && (
              <span style={{
                color: t.progressFill,
                textShadow: `0 0 8px ${t.progressGlow}, 0 0 16px ${t.progressGlow}`,
              }}>&gt;</span>
            )}
          </span>
        )}
        {empty > 0 && (
          <span style={{ color: t.progressTrack }}>
            {'\u00B7'.repeat(empty)}
          </span>
        )}
        <span style={{ color: t.textMuted }}>]</span>
      </div>
    </div>
  );
};

interface SkillTreeProps {
  qualifications: Qualification[];
  userProgress: Record<string, Progress> | null;
  onUpdateProgress: (progress: Progress) => Promise<void>;
  isInstructor: boolean;
  enrollment?: Enrollment | null; // optional: shows deadline badges if provided
}

export const SkillTree: React.FC<SkillTreeProps> = ({
  qualifications,
  userProgress,
  onUpdateProgress,
  isInstructor,
  enrollment,
}) => {
  const [expandedQuals, setExpandedQuals] = useState<Set<string>>(new Set());
  const { t } = useTheme();

  // Use shared progress library with local progress data
  const progress = userProgress || {};

  const localGetQualStatus = (qual: Qualification): QualStatus =>
    getQualStatus(qual, qualifications, progress);

  const localGetProgressPercentage = (qual: Qualification): number =>
    getProgressPercentage(qual, progress);

  const localGetCompletedCount = (qual: Qualification): number =>
    getCompletedCount(qual, progress);

  const localGetRetriesNeeded = (qualId: string, itemId: string): number =>
    getRetriesNeeded(progress, qualId, itemId);

  const getStatusBorder = (status: string): string => {
    switch (status) {
      case 'locked': return t.border;
      case 'available': return `${t.accent}80`;
      case 'in-progress': return `${t.success}80`;
      case 'complete': return `${t.success}80`;
      default: return t.border;
    }
  };

  const getStatusBg = (status: string): string => {
    switch (status) {
      case 'locked': return t.cardBg;
      case 'available': return t.cardBg;
      case 'in-progress': return t.cardBg;
      case 'complete': return t.successBg;
      default: return t.cardBg;
    }
  };

  const toggleExpanded = (qualId: string) => {
    const newSet = new Set(expandedQuals);
    if (newSet.has(qualId)) {
      newSet.delete(qualId);
    } else {
      newSet.add(qualId);
    }
    setExpandedQuals(newSet);
  };

  const sortedQuals = useMemo(
    () => [...qualifications].sort((a, b) => a.order - b.order),
    [qualifications]
  );

  return (
    <div className="space-y-6">
      {sortedQuals.map((qual, index) => {
        const status = localGetQualStatus(qual);
        const isExpanded = expandedQuals.has(qual.id);
        const percentage = localGetProgressPercentage(qual);
        const completedCount = localGetCompletedCount(qual);
        const totalCount = qual.items.length;
        const cardTiming = userProgress ? getQualificationTiming(qual.id, userProgress) : null;
        const cardDeadline = enrollment ? getDeadlineDate(enrollment, qual.id) : null;
        const fmtMono = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
        const fmtLong = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

        // Compute effective start: earliest of first signoff in this skill or
        // completion of previous skill. For the first skill, fall back to enrollment start.
        // If no JQR items are completed, leave blank (null).
        const effectiveStart: Date | null = (() => {
          if (!cardTiming?.firstSignoff && completedCount === 0) return null; // no activity
          const candidates: Date[] = [];
          if (cardTiming?.firstSignoff) {
            candidates.push(cardTiming.firstSignoff instanceof Date ? cardTiming.firstSignoff : new Date(cardTiming.firstSignoff));
          }
          if (index > 0) {
            // Previous skill's last signoff = when it was completed
            const prevTiming = userProgress ? getQualificationTiming(sortedQuals[index - 1].id, userProgress) : null;
            if (prevTiming?.lastSignoff) {
              const prevComplete = localGetQualStatus(sortedQuals[index - 1]) === 'complete';
              if (prevComplete) {
                candidates.push(prevTiming.lastSignoff instanceof Date ? prevTiming.lastSignoff : new Date(prevTiming.lastSignoff));
              }
            }
          } else if (enrollment) {
            // First skill: use enrollment start date
            candidates.push(enrollment.startDate);
          }
          if (candidates.length === 0) return null;
          return candidates.reduce((a, b) => (a < b ? a : b));
        })();

        return (
          <div key={qual.id}>
            {/* Connection Line with Deadline Date */}
            {index > 0 && (() => {
              const prevQual = sortedQuals[index - 1];
              const prevDeadline = enrollment ? getDeadlineDate(enrollment, prevQual.id) : null;
              const fmtShortDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
              const prevDaysLeft = prevDeadline ? getDaysRemaining(prevDeadline) : null;
              const deadlineColor = prevDaysLeft !== null
                ? prevDaysLeft < 0 ? t.fail : prevDaysLeft < 7 ? '#f59e0b' : t.textMuted
                : t.textMuted;

              return (
                <div className="flex flex-col items-center py-1">
                  <div className="h-3 w-0.5 rounded-full" style={{ background: t.border }} />
                  {prevDeadline ? (
                    <div
                      className="text-[10px] font-mono py-0.5"
                      style={{ color: deadlineColor }}
                      title={`Deadline: ${prevDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                    >
                      ⏤ {fmtShortDate(prevDeadline)} ⏤
                    </div>
                  ) : (
                    <div className="h-2" />
                  )}
                  <div className="h-3 w-0.5 rounded-full" style={{ background: t.border }} />
                </div>
              );
            })()}

            {/* Qualification Card */}
            <button
              onClick={() => toggleExpanded(qual.id)}
              disabled={status === 'locked' && !isInstructor}
              className="w-full rounded-lg border-2 p-6 text-left transition-all hover:shadow-lg hover:shadow-black/20 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: getStatusBg(status),
                borderColor: getStatusBorder(status),
                color: status === 'locked' ? t.textMuted : t.textPrimary,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <h3 className="text-lg font-bold" style={{ color: t.textPrimary }}>{qual.shortName}</h3>
                    {qual.optional && (
                      <span
                        className="text-xs font-bold rounded-full px-2 py-1 border whitespace-nowrap"
                        style={{
                          background: `${t.accent}20`,
                          color: t.accent,
                          borderColor: `${t.accent}50`,
                        }}
                      >
                        Optional
                      </span>
                    )}
                    {status === 'locked' && (
                      <span
                        className="text-xs font-bold rounded-full px-2 py-1 border whitespace-nowrap"
                        style={{
                          background: `${t.fail}20`,
                          color: t.fail,
                          borderColor: `${t.fail}50`,
                        }}
                      >
                        Locked
                      </span>
                    )}
                    {status === 'complete' && (
                      <span
                        className="text-xs font-bold rounded-full px-2 py-1 border whitespace-nowrap"
                        style={{
                          background: `${t.success}20`,
                          color: t.success,
                          borderColor: `${t.success}50`,
                        }}
                      >
                        Complete
                      </span>
                    )}
                    {enrollment && status !== 'complete' && (() => {
                      const deadline = getDeadlineDate(enrollment, qual.id);
                      if (!deadline) return null;
                      const days = getDaysRemaining(deadline);
                      const color = days < 0 ? t.fail : days < 7 ? '#f59e0b' : t.textSecondary;
                      const label = days < 0
                        ? `${Math.abs(days)}d overdue`
                        : `${days}d left`;
                      return (
                        <span
                          className="text-xs font-medium rounded-full px-2 py-0.5"
                          style={{ color }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-sm" style={{ color: t.textSecondary }}>{qual.name}</p>
                </div>

                {/* Terminal-style date block */}
                {enrollment && (
                  <div
                    className="flex-shrink-0 font-mono text-[11px] leading-relaxed rounded px-3 py-2"
                    style={{
                      background: 'rgba(0,0,0,0.3)',
                      border: `1px solid ${t.borderSubtle}`,
                      minWidth: '150px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {effectiveStart && (
                      <div style={{ color: t.textMuted }}>
                        <span style={{ color: t.textSecondary }}>start</span>{' '}
                        <span style={{ color: t.accent }}>{fmtMono(effectiveStart)}</span>
                      </div>
                    )}
                    {cardTiming?.lastSignoff && (
                      <div
                        style={{ color: t.textMuted }}
                        title={`Last activity on ${fmtLong(cardTiming.lastSignoff instanceof Date ? cardTiming.lastSignoff : new Date(cardTiming.lastSignoff))}`}
                      >
                        <span style={{ color: t.textSecondary }}>last{' '}</span>
                        <span style={{ color: t.progressFill }}>
                          {fmtMono(cardTiming.lastSignoff instanceof Date ? cardTiming.lastSignoff : new Date(cardTiming.lastSignoff))}
                        </span>
                      </div>
                    )}
                    {cardDeadline && (() => {
                      const daysLeft = getDaysRemaining(cardDeadline);
                      const deadColor = daysLeft < 0 ? t.fail : daysLeft < 7 ? '#f59e0b' : t.accent;
                      return (
                        <div style={{ color: t.textMuted }}>
                          <span style={{ color: t.textSecondary }}>due{' '}</span>
                          <span style={{ color: deadColor }}>{fmtMono(cardDeadline)}</span>
                        </div>
                      );
                    })()}
                    {qual.optional && !effectiveStart && !cardTiming?.lastSignoff && !cardDeadline && (
                      <div style={{ color: t.textMuted, fontStyle: 'italic' }}>[optional]</div>
                    )}
                  </div>
                )}

                {/* Expand Icon */}
                <div className="text-xl flex-shrink-0" style={{ color: t.textMuted }}>
                  {isExpanded ? '▼' : '▶'}
                </div>
              </div>

              {/* ASCII Progress Bar — full width below header row */}
              {qual.items.length > 0 && (
                <AsciiProgressBar
                  percentage={percentage}
                  completedCount={completedCount}
                  totalCount={totalCount}
                  isExpanded={isExpanded}
                  t={t}
                />
              )}
            </button>

            {/* Expanded Content */}
            {isExpanded && qual.items.length > 0 && (() => {
              const timing = userProgress ? getQualificationTiming(qual.id, userProgress) : { firstSignoff: null, lastSignoff: null, daysSpent: null };
              const deadlineDate = enrollment ? getDeadlineDate(enrollment, qual.id) : null;
              const fmtShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

              return (
              <div className="mt-4 ml-6 pb-4 space-y-4">
                {/* Progress indicator bar wrapper */}
                <div className="relative pl-6">
                  {/* Start date label at top of bar — colored like the line start */}
                  {enrollment && (
                    <div
                      className="absolute text-[10px] font-mono"
                      style={{
                        top: '-2px',
                        left: '-4px',
                        transform: 'translate(-100%, -100%)',
                        textAlign: 'right',
                        color: percentage > 0 ? `${t.progressFill}99` : t.textMuted,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtShort(enrollment.startDate)}
                    </div>
                  )}

                  {/* Vertical progress line */}
                  <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ overflow: 'visible' }}>
                    {/* Base muted line */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{ background: t.border }}
                    />
                    {/* Progress overlay */}
                    <div
                      className="absolute left-0 top-0 w-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        height: `${percentage}%`,
                        background: `linear-gradient(to bottom, ${t.border} 0%, ${t.progressFill} 100%)`,
                      }}
                    />
                    {/* Laser beam effect at progress front */}
                    {percentage > 0 && percentage < 100 && (
                      <>
                        {/* Beam trail — fades from transparent to bright */}
                        <div
                          className="absolute"
                          style={{
                            top: `${Math.max(percentage - 18, 0)}%`,
                            height: `${Math.min(18, percentage)}%`,
                            width: '4px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: `linear-gradient(to bottom, transparent, ${t.progressFill}99 70%, ${t.progressFill})`,
                            borderRadius: '2px',
                            filter: 'blur(1px)',
                          }}
                        />
                        {/* Bright tip */}
                        <div
                          className="absolute"
                          style={{
                            top: `${percentage}%`,
                            left: '50%',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: t.progressFill,
                            boxShadow: `0 0 6px 2px ${t.progressGlow}, 0 0 14px 5px ${t.progressGlow}40`,
                          }}
                        />
                      </>
                    )}
                    {/* Full completion glow at bottom */}
                    {percentage === 100 && (
                      <div
                        className="absolute"
                        style={{
                          bottom: 0,
                          left: '50%',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          transform: 'translateX(-50%)',
                          background: t.progressFill,
                          boxShadow: `0 0 10px 3px ${t.progressGlow}`,
                        }}
                      />
                    )}
                  </div>

                  {/* Target date at bottom of bar — colored like progress/laser */}
                  {deadlineDate && (
                    <div
                      className="absolute text-[10px] font-mono"
                      style={{
                        bottom: '-2px',
                        left: '-4px',
                        transform: 'translate(-100%, 100%)',
                        textAlign: 'right',
                        color: getDaysRemaining(deadlineDate) < 0 ? t.fail : percentage === 100 ? t.success : `${t.progressFill}99`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtShort(deadlineDate)}
                    </div>
                  )}

                  {/* Floating progress stat — positioned relative to the pl-6 wrapper */}
                  {percentage > 0 && percentage < 100 && (
                    <div
                      className="absolute"
                      style={{
                        top: `${percentage}%`,
                        left: '-4px',
                        transform: 'translate(-100%, -50%)',
                        textAlign: 'right',
                        lineHeight: '1.1',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span className="text-xs font-mono font-bold" style={{ color: t.progressFill, pointerEvents: 'none' }}>
                        {completedCount}/{totalCount}
                      </span>
                      <br />
                      <span className="text-[10px] font-mono" style={{ color: `${t.progressFill}99`, pointerEvents: 'none' }}>
                        {percentage}%
                      </span>
                      {timing.lastSignoff && (() => {
                        const lastDate = timing.lastSignoff instanceof Date ? timing.lastSignoff : new Date(timing.lastSignoff);
                        const longDate = lastDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                        return (
                          <>
                            <br />
                            <span
                              className="text-[9px] font-mono cursor-default"
                              style={{ color: t.progressFill }}
                              title={`Last activity on ${longDate}`}
                            >
                              {fmtShort(lastDate)}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Discussion Items */}
                  {qual.items.some((i) => i.category === 'discussion') && (
                    <div>
                      <h4 className="text-sm font-bold mt-2 mb-4 uppercase tracking-widest" style={{ color: t.sectionHeading }}>
                        Discussion Items
                      </h4>
                      <div className="space-y-3">
                        {qual.items
                          .filter((i) => i.category === 'discussion')
                          .map((item) => (
                            <JQRItemCard
                              key={`${qual.id}_${item.id}`}
                              item={item}
                              qualificationId={qual.id}
                              progress={userProgress?.[`${qual.id}_${item.id}`]}
                              onUpdate={onUpdateProgress}
                              isInstructor={isInstructor}
                            />
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Demonstration Items */}
                  {qual.items.some((i) => i.category === 'demonstration') && (
                    <div className="mt-6">
                      <h4 className="text-sm font-bold mb-4 uppercase tracking-widest" style={{ color: t.sectionHeading }}>
                        Demonstration Items
                      </h4>
                      <div className="space-y-3">
                        {qual.items
                          .filter((i) => i.category === 'demonstration')
                          .map((item) => (
                            <JQRItemCard
                              key={`${qual.id}_${item.id}`}
                              item={item}
                              qualificationId={qual.id}
                              progress={userProgress?.[`${qual.id}_${item.id}`]}
                              onUpdate={onUpdateProgress}
                              isInstructor={isInstructor}
                            />
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Ridealong Items (with retry support) */}
                  {qual.items.some((i) => i.category === 'ridealong') && (
                    <div className="mt-6">
                      <h4 className="text-sm font-bold mb-4 uppercase tracking-widest" style={{ color: t.sectionHeading }}>
                        Ridealong Events
                      </h4>
                      <div className="space-y-3">
                        {qual.items
                          .filter((i) => i.category === 'ridealong')
                          .map((item) => {
                            const retriesNeeded = localGetRetriesNeeded(qual.id, item.id);
                            const retryCards: React.ReactNode[] = [];

                            for (let r = 1; r <= retriesNeeded; r++) {
                              const retryKey = `${qual.id}_${item.id}_retry_${r}`;
                              retryCards.push(
                                <JQRItemCard
                                  key={retryKey}
                                  item={item}
                                  qualificationId={qual.id}
                                  progress={userProgress?.[retryKey]}
                                  onUpdate={onUpdateProgress}
                                  isInstructor={isInstructor}
                                  retryNumber={r}
                                />
                              );
                            }

                            return (
                              <React.Fragment key={`${qual.id}_${item.id}_group`}>
                                <JQRItemCard
                                  key={`${qual.id}_${item.id}`}
                                  item={item}
                                  qualificationId={qual.id}
                                  progress={userProgress?.[`${qual.id}_${item.id}`]}
                                  onUpdate={onUpdateProgress}
                                  isInstructor={isInstructor}
                                />
                                {/* Nested retries under original event */}
                                {retryCards.length > 0 && (
                                  <div className="ml-8 pl-4 space-y-3 relative">
                                    {/* Connecting line from parent to retries */}
                                    <div
                                      className="absolute left-0 top-0 bottom-0 w-0.5 rounded-full"
                                      style={{ background: t.failBorder }}
                                    />
                                    <div className="text-[10px] font-bold uppercase tracking-widest pl-2" style={{ color: `${t.fail}99` }}>
                                      Retry Attempts
                                    </div>
                                    {retryCards}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            })()}

            {/* Empty State */}
            {isExpanded && qual.items.length === 0 && (
              <div
                className="mt-4 ml-6 rounded-lg px-4 py-3 text-sm italic border"
                style={{ background: t.cardBg, borderColor: t.border, color: t.textMuted }}
              >
                No JQR items defined yet
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
